import React from "react"
import { TextStyle, View, ViewStyle } from "react-native"

import Icons from "@expo/vector-icons/FontAwesome"
import { useAppState, useLayout } from "@react-native-community/hooks"
import { Button, Text } from "app/components/Ignite"
import * as Haptics from "expo-haptics"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { useAuth } from "src/context/AuthContext"
import { useWebsocket } from "src/context/WebsocketContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { platformShadowStyle } from "src/theme/shadows"

// Maximum number of reconnect attempts before showing error (matches WebsocketContext)
const MAX_RECONNECT_ATTEMPTS = 7

/**
 * WebsocketConnectionIndicator (Web version):
 * A web-compatible component that displays a warning message when the WebSocket connection has
 * reached the maximum number of reconnection attempts or when there's a permanent error.
 *
 * This web version uses simple opacity transitions instead of complex Reanimated animations.
 */
export interface WebsocketConnectionIndicatorProps {
  useSafeArea?: boolean
}

export function WebsocketConnectionIndicator({ useSafeArea }: WebsocketConnectionIndicatorProps) {
  const { theme, themed } = useAppTheme()
  const { onLayout } = useLayout()
  const appState = useAppState()
  const insets = useSafeAreaInsets()

  const { isSessionDeactivated } = useAuth()
  const { isConnected, error, setError, reconnectAttempts } = useWebsocket()

  const shouldShowWarning = React.useMemo(() => {
    if (isSessionDeactivated) return false
    return (
      appState === "active" &&
      ((!isConnected && reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) || error)
    )
  }, [appState, isConnected, reconnectAttempts, error, isSessionDeactivated])

  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    setIsVisible(Boolean(shouldShowWarning))
  }, [shouldShowWarning])

  if (!isVisible) {
    return null
  }

  return (
    <View
      style={[
        themed($container),
        { opacity: isVisible ? 1 : 0 },
        useSafeArea && { paddingBottom: insets.bottom },
      ]}
    >
      <View onLayout={onLayout} style={$innerPadding}>
        <Icons name={"warning"} size={32} color={theme.colors.white} style={{ width: 32 }} />
        <View style={$textContainer}>
          <Text style={themed($text)}>
            {typeof error === "string" ? error : "Server disconnected"}
          </Text>
          {!error && reconnectAttempts >= MAX_RECONNECT_ATTEMPTS && (
            <Text style={themed($text)}>Maximum reconnect attempts reached.</Text>
          )}
          {error && (
            <>
              <Button
                text="Attempt to reconnect"
                onPress={() => {
                  // On web, we can't use haptics, so skip it
                  if (Haptics.selectionAsync) {
                    Haptics.selectionAsync()
                  }
                  setError(null)
                }}
                style={$button}
                textStyle={themed($buttonText)}
              />
            </>
          )}
        </View>
      </View>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: "100%",
  backgroundColor: colors.palette.angry500,
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  bottom: 0,
  zIndex: 1000,
  transition: "opacity 0.3s ease-in-out", // CSS transition for web
  ...platformShadowStyle({
    xOffset: 0,
    yOffset: -10,
    shadowColor: colors.shadow,
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 10,
  }),
})

const $innerPadding: ViewStyle = {
  paddingVertical: 10,
  flexDirection: "row",
  alignItems: "center",
}

const $textContainer: ViewStyle = {
  flexDirection: "column",
  marginLeft: 10,
  justifyContent: "center",
}

const $text: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.white,
  fontSize: 12,
  lineHeight: 12,
  textAlign: "center",
})

const $button: ViewStyle = {
  marginTop: 10,
  paddingVertical: 0,
  minHeight: undefined,
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  borderWidth: 1,
  borderColor: "rgba(255, 255, 255, 0.5)",
}

const $buttonText: ThemedStyle<TextStyle> = ({ spacing, colors }) => ({
  margin: 0,
  padding: spacing.xxs,
  fontSize: 14,
  color: colors.white,
})
